import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Sentry } from '@/lib/sentry';
import { FEATURES } from '@/lib/features';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
import { lazy, Suspense } from 'react';
import { Landing } from '@/pages/Landing';
import { NotFound } from '@/pages/NotFound';
import { SignIn } from '@/pages/SignIn';
import { SignUp } from '@/pages/SignUp';
import { CheckEmail } from '@/pages/CheckEmail';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { VerifyEmail } from '@/pages/VerifyEmail';
import { Directory } from '@/pages/Directory';
import { Profile } from '@/pages/Profile';
import { Me } from '@/pages/Me';
import { ProfileEdit } from '@/pages/ProfileEdit';
import { ClaimProfile } from '@/pages/ClaimProfile';
import { RequestVerification } from '@/pages/RequestVerification';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AdminRoute } from '@/components/auth/AdminRoute';

const Feed = lazy(() => import('@/pages/Feed'));
const Messages = lazy(() => import('@/pages/Messages'));
const Conversation = lazy(() => import('@/pages/Conversation'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Groups = lazy(() => import('@/pages/Groups'));
const GroupDetail = lazy(() => import('@/pages/GroupDetail'));
const Events = lazy(() => import('@/pages/Events'));
const EventDetail = lazy(() => import('@/pages/EventDetail'));
const EventNew = lazy(() => import('@/pages/EventNew'));
const Mentorship = lazy(() => import('@/pages/Mentorship'));
const Jobs = lazy(() => import('@/pages/Jobs'));
const JobDetail = lazy(() => import('@/pages/JobDetail'));
const JobNew = lazy(() => import('@/pages/JobNew'));
const Ventures = lazy(() => import('@/pages/Ventures'));
const VentureDetail = lazy(() => import('@/pages/VentureDetail'));
const VentureNew = lazy(() => import('@/pages/VentureNew'));
const BusinessDetail = lazy(() => import('@/pages/BusinessDetail'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const AdminPage  = lazy(() => import('@/pages/admin/AdminPage'));
const LearnKhmer = lazy(() => import('@/pages/LearnKhmer'));
const HeadshotPage = lazy(() => import('@/pages/Headshot'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));

function Loader() {
  return (
    <div className="flex h-32 items-center justify-center text-muted text-sm">
      Loading…
    </div>
  );
}

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <Landing />,
    errorElement: <NotFound />,
  },

  // Auth flows (unauthenticated)
  { path: '/signin', element: <SignIn /> },
  { path: '/signup', element: <SignUp /> },
  { path: '/signup/check-email', element: <CheckEmail /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/auth/verify', element: <VerifyEmail /> },

  // Legal pages (public)
  {
    path: '/privacy',
    element: (
      <Suspense fallback={<Loader />}>
        <Privacy />
      </Suspense>
    ),
  },
  {
    path: '/terms',
    element: (
      <Suspense fallback={<Loader />}>
        <Terms />
      </Suspense>
    ),
  },

  // Headshot generator (public)
  {
    path: '/headshot',
    element: (
      <Suspense fallback={<Loader />}>
        <HeadshotPage />
      </Suspense>
    ),
  },

  // Public read surfaces
  {
    path: '/search',
    element: (
      <Suspense fallback={<Loader />}>
        <SearchPage />
      </Suspense>
    ),
  },
  { path: '/map', element: <Navigate to="/directory?tab=map" replace /> },
  {
    path: '/businesses/:slug',
    element: (
      <Suspense fallback={<Loader />}>
        <BusinessDetail />
      </Suspense>
    ),
  },
  {
    path: '/groups',
    element: (
      <Suspense fallback={<Loader />}>
        <Groups />
      </Suspense>
    ),
  },
  {
    path: '/groups/:slug',
    element: (
      <Suspense fallback={<Loader />}>
        <GroupDetail />
      </Suspense>
    ),
  },
  {
    path: '/events',
    element: FEATURES.events ? (
      <Suspense fallback={<Loader />}><Events /></Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: '/events/:slug',
    element: FEATURES.events ? (
      <Suspense fallback={<Loader />}><EventDetail /></Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: '/mentorship',
    element: FEATURES.mentorship ? (
      <Suspense fallback={<Loader />}><Mentorship /></Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: '/jobs',
    element: FEATURES.jobs ? (
      <Suspense fallback={<Loader />}><Jobs /></Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: '/jobs/:slug',
    element: FEATURES.jobs ? (
      <Suspense fallback={<Loader />}><JobDetail /></Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: '/ventures',
    element: FEATURES.ventures ? (
      <Suspense fallback={<Loader />}><Ventures /></Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: '/ventures/:slug',
    element: FEATURES.ventures ? (
      <Suspense fallback={<Loader />}><VentureDetail /></Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },

  // Admin-only routes — requires verification = 'admin_verified'
  {
    element: <AdminRoute />,
    children: [
      {
        path: '/admin',
        element: (
          <Suspense fallback={<Loader />}>
            <AdminPage />
          </Suspense>
        ),
      },
    ],
  },

  // Protected routes — redirect to /signin if not authenticated
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/directory', element: <Directory /> },
      { path: '/profile/:handle', element: <Profile /> },
      { path: '/profile/:handle/claim', element: <ClaimProfile /> },
      { path: '/me', element: <Me /> },
      { path: '/me/edit', element: <ProfileEdit /> },
      { path: '/me/verify', element: <RequestVerification /> },
      {
        path: '/ventures/new',
        element: FEATURES.ventures ? (
          <Suspense fallback={<Loader />}><VentureNew /></Suspense>
        ) : (
          <Navigate to="/feed" replace />
        ),
      },
      {
        path: '/jobs/new',
        element: FEATURES.jobs ? (
          <Suspense fallback={<Loader />}><JobNew /></Suspense>
        ) : (
          <Navigate to="/feed" replace />
        ),
      },
      {
        path: '/events/new',
        element: FEATURES.events ? (
          <Suspense fallback={<Loader />}><EventNew /></Suspense>
        ) : (
          <Navigate to="/feed" replace />
        ),
      },
      {
        path: '/feed',
        element: (
          <Suspense fallback={<Loader />}>
            <Feed />
          </Suspense>
        ),
      },
      { path: '/connections', element: <Navigate to="/messages" replace /> },
      {
        path: '/messages',
        element: (
          <Suspense fallback={<Loader />}>
            <Messages />
          </Suspense>
        ),
      },
      {
        path: '/messages/:id',
        element: (
          <Suspense fallback={<Loader />}>
            <Conversation />
          </Suspense>
        ),
      },
      {
        path: '/notifications',
        element: (
          <Suspense fallback={<Loader />}>
            <Notifications />
          </Suspense>
        ),
      },
      {
        path: '/learn-khmer',
        element: (
          <Suspense fallback={<Loader />}>
            <LearnKhmer />
          </Suspense>
        ),
      },
    ],
  },

  { path: '*', element: <NotFound /> },
]);
